import { ReduxAction, State } from '../../reducers/reducerType'
import { roomTypes } from './roomTypes'
import roomReducerDefaultState, { PresenterInfo } from '../../reducers/room/defaultState'
import constants, { mediaType as MediaType, trackStatus } from '@bt-react/constants'
import { LocalTrackPublication, LocalParticipant, Participant, Room, TwilioError, LocalAudioTrackPublication, LocalVideoTrackPublication } from 'twilio-video'
import isScreenshareTrack from '../../../lib/isScreenshareTrack'
import trackActions from '../tracks'
import { hideSettingsModal } from '../ui/uiActions'
import username from '../../../lib/username'
import { debug } from '@bt-react/libs/Logger'
import selectIsForcedMuted from '../../selectors/selectIsForcedMuted'
import { updateAudioMute, setAudioSource } from '../tracks/trackActions'
import devices from '@bt-react/libs/devices'
import get from 'lodash/get'
import { AudioSource } from '../../reducers/tracks/tracksReducerDefaultState'
import { addSeatToMap } from '../seatingOrder/seatingOrderSyncFunctions'
import { LayoutsSlice } from '../../slices/layouts'
import { OBSERVER, PRESENTER } from '../../../dataStore/constants/roles'
import selectPresenterBackground from '../../selectors/selectPresenterBackground'
import { setUserBackground } from '../../slices/userSettings/userSettingsSlice'
import { VirtualBackgrounds } from '../../../components/VirtualBackgroundPicker/VirtualBackgroundPicker'
import selectHasGigEnded from '../../selectors/selectHasGigEnded'
import getBTIDsByRole from '../../../lib/getBTIDsByRole'
import { addEntry } from '../../../lib/audit'
import { ParticipantInformationInterface } from '../../reducers/docs/syncUi/types'
import docUpdateAndLog from '../../../lib/docUpdateAndLog'
import updateParticipantInformation from '../../../lib/setParticipantInformation'
import apiGateway from '../../../services/apiGateway'

export const addParticipantToLookup = (sid: string, identity: string): ReduxAction<{ key: string, value: PresenterInfo }> => {

  const payload = {
    key: sid,
    value: {
      BTID: username.getPureUsername(identity),
      role: username.getRoleFromUsername(identity)
    }
  }
  return { type: roomTypes.ADD_PARTICIPANT_TO_LOOKUP, payload }

}

export const removeParticipantFromLookup = (participantSid: string): ReduxAction<string> => ({ type: roomTypes.REMOVE_PARTICIPANT_FROM_LOOKUP, payload: participantSid })
export const participantCamHidden = (identity: string): ReduxAction<string> => ({ type: roomTypes.PARTICIPANT_CAM_HIDDEN, payload: identity })
export const twilioReconnecting = () => ({ type: roomTypes.TWILIO_RECONNECTING })
export const twilioReconnected = () => ({ type: roomTypes.TWILIO_RECONNECTED })
export const twilioRoomDisconnected = () => ({ type: roomTypes.TWILIO_ROOM_DISCONNECTED })
export const presenterConnected = (sid: string, identity: string, local = false): ReduxAction<{ sid: string, identity: string, local: boolean }> => ({ type: roomTypes.PRESENTER_CONNECTED, payload: { sid, identity, local } })
export const presenterDisconnected = (sid: string): ReduxAction<string> => ({ type: roomTypes.PRESENTER_DISCONNECTED, payload: sid })
export const remoteParticipantsAdded = () => ({ type: roomTypes.REMOTE_PARTICIPANTS_ADDED })
export const disconnectUser = () => ({ type: roomTypes.DISCONNECT_USER })
export const disconnectUserSuccess = () => ({ type: roomTypes.DISCONNECT_USER_SUCCESS })
export const participantConnected = (sid: string, identity: string, postSetupEntry = false) => (dispatch, getState) => {

  const { user: { BTID }, room: { participantInformation }, config: { bridgeName } } = getState()
  const { config : { role } } = getState()

  // If a new user joined the room and that user has the same id as me then I will disconnect from the room
  // only do it for observer/presenter to avoid dial in logic conflict.
  if (postSetupEntry) {
    if (identity.replace('brightTALK-observer-', '') === BTID) {
      if (identity.indexOf('brightTALK-observer-') > -1 && role === PRESENTER ||
      identity.indexOf('brightTALK-observer-') === -1 && role === OBSERVER) {
        const { room } = getState().room
        room.disconnect()
      }
    }
  }

  dispatch(addParticipantToLookup(sid, identity))

  debug(`${identity} has connected to the Room`)

  dispatch({ type: roomTypes.PARTICIPANT_CONNECTED, payload: identity })

  // If for some reason the participant is connected but wasn't added to the participant information array we'll add them here
  if (bridgeName === '' && !participantInformation.some(participant => participant.id === identity)) {

    const { docs: { uiDoc }, user: { data: { firstName, lastName, companyName, jobTitle, photoId }} } = getState()

    const participantInfo = {
      BTID,
      id: BTID,
      firstName,
      lastName,
      companyName,
      jobTitle,
      photoId
    }
    updateParticipantInformation(uiDoc, participantInfo)

  }

  const { seatingOrderMap } = getState().docs
  const isLocal = identity === getState().config.username
  const { presenterCount, mediaType } = getState().syncUi

  if (seatingOrderMap && !isLocal) addSeatToMap(identity)

  // if this user is the only presenter, spotlight them if SPOTLIGHT is on
  if (username.isPresenter(identity) && presenterCount === 0 && mediaType === MediaType.SPOTLIGHT) {

    dispatch(setFocus(identity))
    dispatch(setAutoSpotlight(true))

  }

  if (username.isPresenter(identity)) {

    dispatch(presenterConnected(sid, identity, isLocal))

  }

  if (username.isDialIn(identity)) {

    addEntry('PresenterService', 'ParticipantConnectedDialIn', `Participant is Dial-In: ${identity}`)
    dispatch(setDialedInPresenter(identity))

  }

}

export const extendUserSession = (retry = false) => async (dispatch, getState: () => State) => {

  const { BTID } = getState().user

  if (!BTID) return

  try {

    await apiGateway.extendUserSession()

    dispatch({ type: roomTypes.EXTEND_USER_SESSION_SUCCESS })

  } catch (error) {

    dispatch({ type: roomTypes.EXTEND_USER_SESSION_FAILED })

    if (!retry) setTimeout(() => extendUserSession(true), 1000)

  }

}

export const setFocusState = username => ({ type: roomTypes.SET_FOCUS, payload: username })

export const setFocus = focusOn => async (dispatch, getState) => {

  const { docs: { uiDoc } } = getState()

  await docUpdateAndLog(uiDoc, { focusOn }, 'uiDoc')

}

export const setScreenShareFocusOn = screenShareFocus => async (dispatch, getState) => {

  const { docs: { uiDoc } } = getState()

  await docUpdateAndLog(uiDoc, { screenShareFocus }, 'uiDoc')

}

export const clearFocus = () => async (dispatch, getState) => {
  const { docs: { uiDoc } } = getState()

  await docUpdateAndLog(uiDoc, { focusOn: null }, 'uiDoc')

}

const updateDominantSpeakerUiDoc = async (dominantSpeaker: string, uiDoc) => {

  await docUpdateAndLog(uiDoc, { dominantSpeaker }, 'uiDoc')

}

export const setDominantSpeakerIfParticipantIsNull = (participants: string[], mutedParticipants: string[], uiDoc) => {

  const getUnMutedPresenters = participants.filter((r: string) =>
    username.getRoleFromUsername(r) !== OBSERVER)
    .filter((r: string) => !mutedParticipants.includes(r))

  if (getUnMutedPresenters.length === 1) updateDominantSpeakerUiDoc(getUnMutedPresenters[0], uiDoc)

}

export const setDominantSpeaker = (participant) => async (dispatch, getState) => {

  const { docs: { uiDoc }, room, syncUi } = getState()

  /*@ts-ignore*/
  if (participant === null) return setDominantSpeakerIfParticipantIsNull(room.room ? [room.room.localParticipant.identity, ...Array.from(room.room.participants.values()).map(({identity}) => identity)] : [], syncUi.mute, uiDoc)

  const sIds: Map<string, PresenterInfo> = new Map(Object.entries(room.participantLookup))

  const focusOnUser  = sIds.get(participant.sid)

  if (syncUi.mute.includes(focusOnUser?.BTID)) return

  if (focusOnUser) {

    const dominantSpeaker = focusOnUser.BTID

    updateDominantSpeakerUiDoc(dominantSpeaker, uiDoc)

  }
}

export const setAutoSpotlight = (autoSpotlight: boolean) => async (dispatch, getState: () => State) => {

  const { docs: { uiDoc } } = getState()

  await docUpdateAndLog(uiDoc, { autoSpotlight }, 'uiDoc')

}

export const setEventStreamUrl = (url: string) => async (dispatch, getState) => {

  const { docs: { uiDoc } } = getState()

  const result = await docUpdateAndLog(uiDoc, { eventStreamUrl: url }, 'uiDoc')

  dispatch(trackActions.forceLocalMute())

}

export const setLayout = (value: LayoutsSlice) => async (dispatch, getState) => {

  const { docs: { uiDoc } } = getState()

  await docUpdateAndLog(uiDoc, { layouts: value }, 'uiDoc')

}

export const participantDisconnected = (sid: string, identity: string) => async (dispatch, getState: () => State) => {

  const { syncUi: { focusOn, autoSpotlight }, room, config  } = getState()

  //if we have more than one users with the same identity it means that this event
  //should be ignored by the app because its a delayed disconnect event.
  //We should just remove the old SID from any lists that contain it.
  const isDuplicateEntry = Object.values(room.participantLookup).filter((pa) => pa.BTID === identity).length > 1

  if (config.bridgeName !== "") {
    const { firstName, lastName } = room.participantInformation.find(({id}) => id === identity) || { firstName: 'null', lastName: 'null'}
    addEntry('PresenterService', 'ParticipantDisconnectedAction', `bridge: ${config.bridgeName} - ${firstName} ${lastName} disconnected and IS DUPLICATE? ${isDuplicateEntry} - user_id:${identity} sid:${sid}`, 'info', identity)
  }
  //we always want to be doing this cleanup
  dispatch(removeParticipantFromLookup(sid))
  if (username.isPresenter(identity)) {

    dispatch(presenterDisconnected(sid))

  }

  if (isDuplicateEntry) return

  if (focusOn === identity && !autoSpotlight) dispatch(setAutoSpotlight(true))

  debug(`${identity} has disconnected to the Room`)

  dispatch({ type: roomTypes.PARTICIPANT_DISCONNECTED, payload: { identity, sid }})


  await updateFocusOn(identity)(dispatch, getState)
  await updateScreenShareFocus(identity)(dispatch, getState)

  if (username.isDialIn(identity)) {

    dispatch(removeDialedInPresenter(identity))

  }

}

export const participantReconnecting = (error: TwilioError) => dispatch => {

  addEntry('PresenterService',  'TwilioParticipantReconnecting', get(error, 'message'))
  dispatch(twilioReconnecting())

}

export const participantReconnected = (room: Room) => dispatch => {

  debug(`Reconnected to the Room (Room Event)`)

  dispatch(twilioReconnected())
  dispatch(networkQualityChange(5))
  dispatch(trackActions.verifyEnabledAudioTracks(room))
  dispatch(trackActions.verifyEnabledVideoTracks(room))

}
export const addRemoteParticipants = (participants: Map<Participant.SID, Participant>) => (dispatch, getState) => {

  for (const [, { sid, identity, audioTracks, videoTracks }] of participants.entries()) {

    dispatch(participantConnected(sid, identity))

    for (const audioTrack of audioTracks) {

      const [ trackSid, trackValues ] = audioTrack
      if (trackValues.isTrackEnabled) dispatch(trackActions.setEnabledAudioTrackEntry(trackSid))

    }

    for (const videoTrack of videoTracks) {

      const [ trackSid, trackValues ] = videoTrack
      if (trackValues.isTrackEnabled) dispatch(trackActions.setEnabledVideoTrackEntry(trackSid))

    }

  }

  dispatch(remoteParticipantsAdded())

}

export const updateFocusOn = (identity: string) => async (dispatch, getState) => {

  const {
    docs: { uiDoc },
    syncUi: { focusOn = 'NO-VALID-NAME' }
  } = getState()

  if (focusOn !== identity) return

  await docUpdateAndLog(uiDoc, { focusOn: roomReducerDefaultState.focusOn }, 'uiDoc')

}

export const updateScreenShareFocus = (identity: string) => async (dispatch, getState) => {

  const {
    docs: { uiDoc },
    syncUi: { mediaType, screenShareFocus },
    layouts: { presenterLayout }
  } = getState()

  if (screenShareFocus !== username.getPureUsername(identity) || mediaType !== MediaType.SCREENSHARE) return


  await docUpdateAndLog(
    uiDoc,
    {
      screenShareFocus: roomReducerDefaultState.screenShareFocus,
      mediaType: presenterLayout.presenterLayoutsActive === true ? MediaType.SPOTLIGHT : MediaType.WEBCAM
    },
    'uiDoc'
  )
}

export const participantMuted = (identity: string) => (dispatch, getState) => {

  const { ui } = getState()

  if (!ui.forcedLocalMute) dispatch({ type: roomTypes.PARTICIPANT_MUTED, payload: identity })

}

export const participantUnmuted = (identity: string): ReduxAction<string> =>
  ({ type: roomTypes.PARTICIPANT_UNMUTED, payload: identity })
export const participantCamVisible = (identity: string): ReduxAction<string> =>
  ({ type: roomTypes.PARTICIPANT_CAM_VISIBLE, payload: identity })
export const participantInformationAdded = (): ReduxAction => ({ type: roomTypes.PARTICIPANT_INFORMATION_ADDED })
export const participantInformationError = (): ReduxAction => ({ type: roomTypes.PARTICIPANT_INFORMATION_ERROR })
export const setParticipantInformation = (participantInformation: ParticipantInformationInterface[]): ReduxAction<ParticipantInformationInterface[]> => ({ type: roomTypes.SET_PARTICIPANT_INFORMATION, payload: participantInformation })

export const localTrackPublished = (localParticipant: LocalParticipant, publication: LocalTrackPublication) =>
  (dispatch, getState: () => State) => {

  const { trackSid } = publication
  const { sid } = localParticipant
  const {
    docs: {
      uiDoc
    },
    room: {
      playIn,
      presenters
    },
    syncUi: {
      mediaType,
      mute
    },
    ui: {
      forcedLocalMute
    },
    user: {
      BTID = ''
    }
  } = getState()

  if (publication.kind === 'audio') {

    if (selectIsForcedMuted(getState())) {

      debug('Should be force-muted')
      dispatch(trackActions.forceLocalMute())

    }
    else {

      debug('Checking muted status')
      updateAudioMute(mute)

    }

  }

  dispatch(trackActions.setTrackStatus(sid, trackSid, trackStatus.STAGED))

  if (publication.kind === 'video' && isScreenshareTrack(publication as LocalVideoTrackPublication)) {

    if (playIn && mediaType === constants.mediaType.PLAY_IN) {

      playIn.inactive()

    }

    if (uiDoc) docUpdateAndLog(uiDoc, {screenShareFocus: BTID, mediaType: MediaType.SCREENSHARE })

    const presenter = presenters.get(sid)

    if (presenter) {

      const unstage = [ ...presenter.tracks ]
        .filter(([currTrackSid, { kind, status }]) =>
          kind === 'video' && currTrackSid !== trackSid && status === 'STAGED'
        )
        .map(([ trackSid ]) => trackSid)

      for (const currTrackSid of unstage) {

        dispatch(trackActions.setTrackStatus(sid, currTrackSid, trackStatus.UNSTAGED))

      }

    }

  }

}
export const setDialedInPresenter = (identity: string): ReduxAction<string> => ({ type: roomTypes.SET_DIALED_IN_PRESENTER, payload: username.getPureUsername(identity) })
export const removeDialedInPresenter = (identity: string): ReduxAction<string> => ({ type: roomTypes.REMOVE_DIALED_IN_PRESENTER, payload: username.getPureUsername(identity) })
export const setMessageCode = (code: number) => ({ type: roomTypes.SET_MESSAGE_CODE, payload: code })
export const resetUIDocGoingLiveFlag = () => (dispatch, getState: () => State) => {

  const { uiDoc } = getState().docs

  if (!uiDoc) return

  docUpdateAndLog(uiDoc, { goingLive: false })

}

export const setBookingStatus = (status) => (dispatch, getState:() => State) => {
  const { bookingDoc } = getState().docs

  if (!bookingDoc) return

  try {

    bookingDoc.update({ status: status })

  }
  catch (e) {

    debug(e)

  }

}
export const tearDownGig = () => (dispatch, getState: () => State) => {

  const { room } = getState().room

  if (!room) return dispatch({ type: roomTypes.TEAR_DOWN_ROOM_ERROR })

  function endtracks (publications: IterableIterator<LocalAudioTrackPublication | LocalVideoTrackPublication>) {

    for (const publication of publications) {

      try {

        publication.track.stop()
        publication.track.removeAllListeners()

      }
      catch (e) {

        debug(e.message)

      }

      try {

        publication.unpublish()

      }
      catch (e) {

        debug(e.message)

      }

      try {

        publication.track.detach()

      }
      catch (e) {

        debug(e.message)

      }

    }

  }

  endtracks(room.localParticipant.audioTracks.values())
  endtracks(room.localParticipant.videoTracks.values())

  try {

    room.disconnect()

  }
  catch (e) {

    debug(e.message)

  }

  dispatch({ type: roomTypes.TEAR_DOWN_ROOM_SUCCESS })

}
export const setSettings = (settings) => async (dispatch, getState: () => State) => {

  const {
    selectedAudioDeviceID,
    selectedVideoDeviceID,
    audioSource
  } = getState().tracks

  const state = getState()
  const getPresenterBackground = selectPresenterBackground()
  const background = getPresenterBackground(state)
  if (background.type !== settings.background) {

    dispatch(setUserBackground(settings.background))
  }

     const payloadAudioDeviceId = get(settings, 'selectedMicrophone.deviceId', selectedAudioDeviceID)
    const payloadVideoDeviceId = get(settings, 'selectedWebcam.deviceId', selectedVideoDeviceID)
    const _audioSource: AudioSource | undefined = get(settings, 'audioSource')
  
    if (payloadAudioDeviceId !== selectedAudioDeviceID) {
  
      dispatch(trackActions.setSelectedAudioDevice(payloadAudioDeviceId))
  
    }
  
    if (payloadVideoDeviceId !== selectedVideoDeviceID || background.type !== settings.background || settings.background === VirtualBackgrounds.IMAGE) {

      dispatch(trackActions.setSelectedVideoDevice(payloadVideoDeviceId, { customImage: background.customImage, type: settings.background}))
  
    }
  
    if (_audioSource && audioSource !== _audioSource) dispatch(setAudioSource(_audioSource))




  dispatch(hideSettingsModal())

}

export const addMissingUserToParticipantsDetails = ()  => async (dispatch, getState) => {

  const { user, syncUi, room: { participantLookup }, config: { bridgeName } } = getState()

  if (bridgeName !== '') return

  const participantLookupArr = Object.values(participantLookup).map((participant: any) => participant)
    .filter(participant => participant.role === PRESENTER || participant.role === OBSERVER)

  const isUserInLookup = participantLookupArr.some(participant => user.BTID === participant.BTID)

  if (syncUi.participantInformation.length < 1 || !isUserInLookup) return

  const isUserInParticipantArray = syncUi.participantInformation.some(participant => user.BTID === participant.BTID)

  if (isUserInParticipantArray) return

  const formattedParticipant: ParticipantInformationInterface = {
    BTID : user.BTID,
    id : user.BTID,
    firstName: user.data.firstName,
    lastName: user.data.lastName,
    jobTitle: user.data.jobTitle,
    companyName: user.data.companyName,
    photoId: user.data.photoId
  }

  dispatch({ type: roomTypes.SET_MISSING_PARTICIPANT_INFORMATION, payload: formattedParticipant })

}

export const setMediaType = (mediaTypeToUpdate: MediaType) => async (dispatch, getState) => {

  const { docs: { uiDoc }, syncUi: { mediaType } } = getState()

  if (!uiDoc) return

  if (mediaType === MediaType.WEBCAM && mediaTypeToUpdate !== MediaType.SPOTLIGHT) dispatch(setAutoSpotlight(true))

  await docUpdateAndLog(uiDoc, { mediaType: mediaTypeToUpdate })

}
export const setMediaTypeState = (mediaType: MediaType) => ({ type: roomTypes.SET_MEDIA_TYPE_STATE, payload: mediaType })

export const reconnectRoom = (participant?) => async (dispatch, getState) => {

// if the localParticipant's id matches the screensharer's id, then  screensharefocus and mediatype to null and to webcam respectively.
  if(getState().user.BTID===getState().docs.uiDoc?.data.screenShareFocus){

    await docUpdateAndLog(getState().docs.uiDoc, { screenShareFocus: null, mediaType: MediaType.WEBCAM })

    dispatch(setMediaType(MediaType.WEBCAM))

  }

  dispatch(twilioRoomDisconnected())
  const {
    ui: {
      isEndRequested
    }
  } = getState()

  if (!isEndRequested && !selectHasGigEnded(getState())) return window.location.reload()

  if (Reflect.has(participant, 'tracks')) {

    for (const [, publication] of participant.tracks) {

      publication.track.stop()
      publication.track.detach()
      participant.unpublishTrack(publication.track)

    }

  }

}
export const networkQualityChange = level => ({ type: roomTypes.NETWORK_QUALITY_CHANGE, payload: level })
export const unsetFocus = () => ({ type: roomTypes.UNSET_FOCUS })
export const setPresenterCount = (count: number) => ({ type: roomTypes.SET_PRESENTER_COUNT, payload: count })
export const updateVideoDisplay = hiddenIDs => async (dispatch, getState) => {

  const state = getState()
  const { room: { room, participantLookup }, user: { BTID }  } = state


  if (!get(room, 'localParticipant')) return null

  /** If the current user is in list of hidden participants */
  if (hiddenIDs.indexOf(BTID) !== -1) {

    for (const [, tracks] of room.localParticipant.videoTracks) {

      if (isScreenshareTrack(tracks)) continue

      if (tracks.track.isEnabled) {
        tracks.track.disable()

      }

    }

  }
  else {
    for (const [, tracks] of room.localParticipant.videoTracks) {

      if (!tracks.track.isEnabled) {

        if (isScreenshareTrack(tracks)) continue

        tracks.track.enable()

      }

    }

  }

  for (const remoteParticipantBTID of getBTIDsByRole(participantLookup, 'PRESENTER')) {

    if (remoteParticipantBTID !== BTID) {

      if (hiddenIDs.indexOf(remoteParticipantBTID) !== -1) {

        dispatch(participantCamHidden(remoteParticipantBTID))

      }
      else {

        dispatch(participantCamVisible(remoteParticipantBTID))

      }

    }

  }

}
export const updatePermissions = () => async (dispatch) => {

  const permissions = await devices.checkPermissions()
  dispatch({ type: roomTypes.SET_DEVICE_PERMISSIONS, payload: permissions })

}

export default {
  addRemoteParticipants,
  localTrackPublished,
  participantCamHidden,
  participantCamVisible,
  participantConnected,
  participantDisconnected,
  participantInformationAdded,
  participantInformationError,
  participantMuted,
  participantReconnected,
  participantReconnecting,
  participantUnmuted,
  removeDialedInPresenter,
  resetUIDocGoingLiveFlag,
  setDialedInPresenter,
  setParticipantInformation,
  setSettings,
  setFocus,
  setFocusState,
  setAutoSpotlight,
  tearDownGig,
  twilioRoomDisconnected,
  addParticipantToLookup,
  removeParticipantFromLookup,
  remoteParticipantsAdded,
  disconnectUser,
  disconnectUserSuccess,
  addMissingUserToParticipantsDetails,
  setMediaType,
  setMediaTypeState,
  reconnectRoom,
  networkQualityChange,
  unsetFocus,
  updatePermissions
}
